/* eslint-disable no-use-before-define */
import React, { useContext, useEffect, useMemo } from 'react';
import _ from 'underscore';
import hoistNonReactStatics from 'hoist-non-react-statics';
import { getDisplayName } from '@bingads-webui-react/hoc-utils';
import { InstrumentationContext } from '@bingads-webui-react/hoc-with-instrumentation';
import { ActivityContext } from '@bingads-webui-react/hoc-with-signal';

const TelemetryContext = React.createContext({});

const noopTelemetry = {
  trace: _.noop,
  error: _.noop,
};

const EmptyObject = Object.freeze({});

// TODO: maybe bind this only in dev mode?
const debug = window.console;

export function withTelemetry(label = '', Component) {
  const HoC = (props) => {
    const { label: inheritedLabel = '', logSource } = useContext(TelemetryContext) || EmptyObject;
    const instrumentation = useContext(InstrumentationContext);
    const telemetry = useMemo(() => logSource || createActivity(instrumentation), [instrumentation, logSource]);

    useEffect(() => {
      if (!telemetry) {
        debug.error(`Missing instrumentation context (${label})`);
      }
    }, [telemetry]);

    // NOTE: if this HoC does not add any new information, just render the wrapped component as is
    // this simplifies the decision process regarding whether to wrap a component or not --
    // just always wrap it, if you need to log something; if you don't want to create a local telemetry namespace
    // the HoC will try to reuse an existing telemetry context,
    // while making every effort to ensure such a context exists
    return (_.isEmpty(label) && (logSource || !telemetry)
      ? <Component {...props} />
      : (
        <TelemetryContext.Provider value={{ label: _.isEmpty(inheritedLabel) ? label : `${inheritedLabel}.${label}`, logSource: telemetry }}>
          <Component {...props} />
        </TelemetryContext.Provider>
      )
    );
  };

  HoC.displayName = `WithTelemetry(${getDisplayName(Component)})`;
  hoistNonReactStatics(HoC, Component);

  return HoC;
}

export function useTelemetry() {
  const { label = '', logSource } = useContext(TelemetryContext) || EmptyObject;
  // fallback to any available activity
  const activity = useContext(ActivityContext);
  const telemetry = useMemo(() => logSource || activity || noopTelemetry, [activity, logSource]);

  useEffect(() => {
    if (telemetry === noopTelemetry) {
      debug.error(`Missing telemetry context (${label})`);
    }
  }, [label, telemetry]);

  return useMemo(() => ({
    trace: msg => telemetry.trace(`${label}: ${msg}`),
    error: error => telemetry.error(`${label}: ${error}`),
  }), [label, telemetry]);
}

function createActivity(instrumentation) {
  const scenario = instrumentation && _.isFunction(instrumentation.createScenario) && instrumentation.createScenario({ scenarioName: '-' });
  const activity = scenario && _.isFunction(scenario.createActivity) && scenario.createActivity('-');

  return activity;
}
